<template>
  <Transition name="alert">
    <div v-if="alert.show" class="relative rounded-2xl w-[340px]" :class="borderClass">
      <div class="flex h-full items-center justify-between rounded-2xl bg-bg-canvas py-4">
        <div class="flex items-center gap-3 truncate px-3">
          <Icon :name="iconName" :class="iconClass" :strokeWidth="1.5" />
          <p class="text-wrap font-medium text-strongest font-inter">
            {{ alert.message }}
          </p>
        </div>
        <Button @click="$emit('close-alert')" size="xs" variant="paper-primary" class="mr-3">
          <template #icon>
            <Icon name="X" class="min-h-3 min-w-3 h-3 w-3" :strokeWidth="3" />
          </template>
        </Button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed } from "vue";
import { Icon, Button } from "@/components";

const props = defineProps({
  alert: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close-alert"]);

const typeConfig = {
  success: {
    icon: "CircleCheck",
    iconClass: "min-h-6 min-w-6 h-6 w-6 text-success",
    borderStyle: "border-gradient-success",
  },
  error: {
    icon: "CircleX",
    iconClass: "min-h-6 min-w-6 h-6 w-6 text-error ",
    borderStyle: "border-gradient-error",
  },
  info: {
    icon: "Info",
    iconClass: "min-h-6 min-w-6 h-6 w-6 text-blue-600",
    borderStyle: "border-gradient-info",
  },
};

const config = computed(() => typeConfig[props.alert.type] || typeConfig.info);
const iconName = computed(() => props.alert.icon || config.value.icon);
const iconClass = computed(() => config.value.iconClass);
const borderClass = computed(() => config.value.borderStyle);
</script>

<style scoped>
.border-gradient-success,
.border-gradient-error,
.border-gradient-info {
  position: relative;
  background: transparent;
}

.border-gradient-success::before,
.border-gradient-error::before,
.border-gradient-info::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.6px;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.border-gradient-success::before {
  background: linear-gradient(to right, var(--text-success), var(--outline-dividers) 50%);
}

.border-gradient-error::before {
  background: linear-gradient(to right, var(--text-error), var(--outline-dividers) 50%);
}

.border-gradient-info::before {
  background: linear-gradient(to right, #0ea5e9, var(--outline-dividers) 50%);
}

.alert-enter-active,
.alert-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.alert-enter-from,
.alert-leave-to {
  transform: translateX(calc(100% + 2rem));
  opacity: 0;
}
</style>
